/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getLocalStorageItem, setLocalStorageItem } from '@atlassian/search-common';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import {
	onThirdPartyBannerAppClicked,
	onThirdPartyBannerDismissed,
	onThirdPartyBannerShown,
} from '../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';

import { AppButton } from './app-button';
import { THIRD_PARTY_BANNER_DISMISSED_KEY } from './constants';
import { messages } from './messages';
import type { DisplayableApp, ThirdPartyBannerProps, ThirdPartyConfigForBanner } from './types';

const styles = cssMap({
	container: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.150'),
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.medium'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
	},
	content: {
		flex: '1',
	},
});

/**
 * Filters and matches recommended apps against third-party configs.
 * Only returns apps where the config exists and userNeedsOAuth is true.
 */
function getDisplayableApps(
	recommendedApps: string[],
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigForBanner>>,
): DisplayableApp[] {
	return recommendedApps
		.filter((appKey) => {
			const config = thirdPartyConfigs[appKey];
			return config && config.userNeedsOAuth === true;
		})
		.map((appKey) => ({
			key: appKey,
			config: thirdPartyConfigs[appKey] as ThirdPartyConfigForBanner,
		}));
}

/**
 * A banner component that displays recommended third-party apps for the user to connect.
 * Only shows apps that:
 * 1. Are recommended by the API
 * 2. Have a matching thirdPartyConfig
 * 3. Have userNeedsOAuth set to true
 */
export function ThirdPartyBanner({
	recommendedApps,
	thirdPartyConfigs,
	loading = false,
	onAppClick,
	testId = 'sain-third-party-banner',
	useOAuthHandlers,
}: ThirdPartyBannerProps) {
	const { formatMessage } = useIntl();
	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const [isDismissed, setIsDismissed] = useState(
		() => getLocalStorageItem(THIRD_PARTY_BANNER_DISMISSED_KEY) === true,
	);
	const bannerShownEventFiredRef = useRef(false);

	const displayableApps = useMemo(
		() => (thirdPartyConfigs ? getDisplayableApps(recommendedApps, thirdPartyConfigs) : []),
		[recommendedApps, thirdPartyConfigs],
	);

	// Fire banner shown analytics event only once when the banner is rendered
	useEffect(() => {
		if (
			!bannerShownEventFiredRef.current &&
			!isDismissed &&
			!loading &&
			displayableApps.length > 0 &&
			fireAnalyticsEvent
		) {
			fireAnalyticsEvent(
				onThirdPartyBannerShown({
					...commonAttributes,
					displayedAppsCount: displayableApps.length,
				}),
			);
			bannerShownEventFiredRef.current = true;
		}
	}, [
		isDismissed,
		loading,
		displayableApps,
		fireAnalyticsEvent,
		recommendedApps.length,
		commonAttributes,
	]);

	const handleAppClick = (appKey: string, config: ThirdPartyConfigForBanner, appIndex: number) => {
		onAppClick?.(appKey, config);

		if (fireAnalyticsEvent) {
			fireAnalyticsEvent(
				onThirdPartyBannerAppClicked({
					...commonAttributes,
					appKey,
					appIndex,
					displayedAppsCount: displayableApps.length,
				}),
			);
		}
	};

	const handleDismiss = () => {
		if (fireAnalyticsEvent) {
			fireAnalyticsEvent(
				onThirdPartyBannerDismissed({
					...commonAttributes,
					displayedAppsCount: displayableApps.length,
				}),
			);
		}

		setLocalStorageItem(THIRD_PARTY_BANNER_DISMISSED_KEY, 'true');
		setIsDismissed(true);
	};

	// Don't render if dismissed, loading, or no apps to display
	if (isDismissed || loading || displayableApps.length === 0) {
		return null;
	}

	return (
		<Box xcss={styles.container} testId={testId}>
			<Inline space="space.100">
				<Stack space="space.100" xcss={styles.content}>
					<Heading size="xxsmall" as="h3">
						<FormattedMessage
							{...messages.bannerMessage}
							values={{
								text: (text) => (
									<Text size="small" color="color.text.subtle">
										{text}
									</Text>
								),
							}}
						/>
					</Heading>
					<Inline space="space.100" shouldWrap>
						{displayableApps.map(({ key, config }, index) => (
							<AppButton
								key={key}
								appKey={key}
								config={config}
								onAppClick={(appKey, appConfig) => handleAppClick(appKey, appConfig, index)}
								testId={`${testId}-app-${key}`}
								useOAuthHandlers={useOAuthHandlers}
							/>
						))}
					</Inline>
				</Stack>
				<IconButton
					icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
					label={formatMessage(messages.dismissButtonLabel)}
					appearance="subtle"
					spacing="compact"
					onClick={handleDismiss}
					testId={`${testId}-dismiss`}
				/>
			</Inline>
		</Box>
	);
}
